/**
 * Rule Runs list — trace records for rule evaluations (read-only, append-only).
 * A run table leads with **status** and **started**, then the rule it ran and
 * how long it took; foreign keys resolve to readable names via `RefLink`, never
 * raw ids. Loading / empty / error come from the shared RunsDataTable states.
 */
import { useMemo } from "react";
import { useNavigation } from "@refinedev/core";
import type { ColumnDef } from "@tanstack/react-table";
import type { RuleRun } from "@aos/shared";
import { StatusBadge } from "@/components/app/StatusBadge";
import { PageHeader } from "@/components/layout/PageHeader";
import { RefLink } from "@/components/inspector";
import { RunsDataTable } from "../_shared/RunsDataTable";
import { formatDateTime } from "../_shared/runs-ui";

/** Human run duration from two ISO timestamps; em-dash when still running. */
function runDuration(
  start: string | null | undefined,
  end: string | null | undefined,
): string {
  if (!start || !end) return "—";
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (Number.isNaN(ms) || ms < 0) return "—";
  if (ms < 1000) return `${ms}ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(s < 10 ? 1 : 0)}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${Math.round(s % 60)}s`;
}

export function RuleRunList() {
  const { show } = useNavigation();

  const columns = useMemo<ColumnDef<RuleRun>[]>(
    () => [
      {
        id: "status",
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => <StatusBadge status={row.original.status} />,
      },
      {
        id: "started_at",
        accessorKey: "started_at",
        header: "Started",
        cell: ({ row }) => (
          <span className="text-muted-foreground">
            {formatDateTime(row.original.started_at)}
          </span>
        ),
      },
      {
        id: "duration",
        header: "Duration",
        enableSorting: false,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {runDuration(row.original.started_at, row.original.finished_at)}
          </span>
        ),
      },
      {
        id: "rule_id",
        accessorKey: "rule_id",
        header: "Rule",
        enableSorting: false,
        cell: ({ row }) => (
          <span onClick={(e) => e.stopPropagation()}>
            <RefLink resource="rules" id={row.original.rule_id} />
          </span>
        ),
      },
      {
        id: "event_id",
        accessorKey: "event_id",
        header: "Event",
        enableSorting: false,
        cell: ({ row }) => (
          <span onClick={(e) => e.stopPropagation()}>
            <RefLink resource="events" id={row.original.event_id} />
          </span>
        ),
      },
      {
        id: "artifact_id",
        accessorKey: "artifact_id",
        header: "Artifact",
        enableSorting: false,
        cell: ({ row }) => (
          <span onClick={(e) => e.stopPropagation()}>
            <RefLink resource="artifacts" id={row.original.artifact_id} />
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Rule Runs"
        subtitle="One trace record per rule evaluation. Open a run for its guard, effect, and context packet."
        icon="ruleRuns"
      />
      <RunsDataTable<RuleRun>
        resource="rule-runs"
        columns={columns}
        sortField="started_at"
        onRowClick={(record) => show("rule-runs", record.id)}
        emptyMessage="No rule runs yet."
      />
    </div>
  );
}

export default RuleRunList;
